import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logAudit } from '@/lib/audit';

const IDLE_LIMIT_MS = 15 * 60 * 1000; // 15 minutes
const WARNING_MS = 60 * 1000; // 60-second warning before logout
const EVENTS: (keyof DocumentEventMap)[] = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];

export function useIdleTimeout() {
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warned = useRef(false);
  const { toast } = useToast();

  const signOut = useCallback(async () => {
    await logAudit({
      action: 'auth.sign_out',
      resource_type: 'auth',
      metadata: { reason: 'idle_timeout' },
    });
    await supabase.auth.signOut();
  }, []);

  const resetTimers = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    warned.current = false;

    warningTimer.current = setTimeout(() => {
      warned.current = true;
      toast({
        title: 'Session expiring soon',
        description: 'You will be signed out in 60 seconds due to inactivity.',
        variant: 'destructive',
      });
    }, IDLE_LIMIT_MS - WARNING_MS);

    idleTimer.current = setTimeout(() => {
      signOut();
    }, IDLE_LIMIT_MS);
  }, [signOut, toast]);

  useEffect(() => {
    // Only run when there's an active session
    let active = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session || !active) return;

      resetTimers();
      EVENTS.forEach((e) => document.addEventListener(e, resetTimers, { passive: true }));
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        resetTimers();
      } else {
        // Signed out — clear timers
        if (idleTimer.current) clearTimeout(idleTimer.current);
        if (warningTimer.current) clearTimeout(warningTimer.current);
      }
    });

    return () => {
      active = false;
      EVENTS.forEach((e) => document.removeEventListener(e, resetTimers));
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (warningTimer.current) clearTimeout(warningTimer.current);
      subscription.unsubscribe();
    };
  }, [resetTimers]);
}
